import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Alerts } from "@components/common/AlertModal";
import axios, { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../store/reducer";
import { useNavigate } from "react-router-dom";
import { colors, Title1, Title2 } from "@utils/ui_palette";
import { rgba } from "emotion-rgba";
import PageNavigation from "@components/common/PageNavigation";

interface IUser {
  id: number;
  createdAt: string;
  login_id: string;
  phone_num: string;
  useremail: string;
  username: string;
}

function UserDetail() {
  const navigation = useNavigate();
  const accessToken = useSelector(
    (state: RootState) => state.user.userInfo.accessToken
  );
  const [userInfo, setUserInfo] = useState<null | IUser[]>(null);
  const [userTotal, setUserTotal] = useState<number>(0);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [pagination, setPagination] = useState<number>(1);

  const getUser = useCallback(
    async (page: number) => {
      setPagination(page);

      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API}/admin/users`,
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        setUserInfo(data.user_list);
        setUserTotal(data.total);
        setTotalPage(data.total_page);
      } catch (error) {
        const errorResponse = (error as AxiosError).response;

        if (errorResponse && (errorResponse.data as any).message) {
          Alerts.show({
            title: (errorResponse.data as any).message,
          });
        }
      }
    },
    [accessToken, setUserInfo, setUserTotal, setTotalPage]
  );

  useEffect(() => {
    getUser(1);
  }, [getUser]);

  return (
    <Container>
      <TitleBox>
        <Title1 align="left">Users List</Title1>
        <Title2>Total {userTotal} users</Title2>
      </TitleBox>

      <UserListGroup>
        <UserListItems>
          <Row isHeader width={5}>
            ID
          </Row>
          <Row isHeader width={10}>
            Login ID
          </Row>
          <Row isHeader width={10}>
            Name
          </Row>
          <Row isHeader width={25}>
            Email
          </Row>
          <Row isHeader width={20}>
            Phone Number
          </Row>
          <Row isHeader width={20}>
            Created Date
          </Row>
        </UserListItems>

        {userInfo &&
          userInfo.length > 0 &&
          userInfo.map((user) => (
            <UserListItems
              onClick={() => navigation(`/user-detail/${user.id}`)}
            >
              <Row isHeader={false} width={5}>
                {user.id}
              </Row>
              <Row isHeader={false} width={10}>
                {user.login_id}
              </Row>
              <Row isHeader={false} width={10}>
                {user.username}
              </Row>
              <Row isHeader={false} width={25}>
                {user.useremail}
              </Row>
              <Row isHeader={false} width={20}>
                {user.phone_num}
              </Row>
              <Row isHeader={false} width={20}>
                {user.createdAt}
              </Row>
            </UserListItems>
          ))}
      </UserListGroup>

      <PagenationContain>
        <PageNavigation
          activePage={pagination}
          itemsCountPerPage={10}
          totalItemsCount={totalPage}
          pageRangeDisplayed={5}
          onChange={(page) => getUser(page)}
        />
      </PagenationContain>
    </Container>
  );
}

const Container = styled.div`
  width: 1024px;
  padding: 20px;
`;

const PagenationContain = styled.div`
  margin-top: 80px;
`;

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const UserListGroup = styled.div`
  margin-top: 30px;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid ${colors.Gray30};
  background-color: ${colors.White};
`;

const UserListItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 20px;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.Gray30, 0.3)};
  }
`;

const Row = styled.div<{ width: number; isHeader: boolean }>`
  display: flex;
  align-items: center;
  text-align: center;
  flex-basis: ${(props) => `${props.width}%`};
  height: 50px;
  font-weight: ${(props) => (props.isHeader ? 700 : 400)};
  color: ${(props) => (props.isHeader ? colors.Primary50 : colors.Black)};
`;

export default UserDetail;
